import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Box } from '@sorare/core/src/atoms/ui/Box';

import { ManagerDivision } from 'components/rivals/ManagerDivision';
import { SeasonProgressGraph } from 'components/rivals/seasons/SeasonProgressGraph';

import { SeasonProgressGraphWithLegend_footballRivalsPublicManagerInterface } from './__generated__/index.graphql';

const Header = styled.div`
  padding-bottom: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-150);
`;

type Props = {
  manager?: SeasonProgressGraphWithLegend_footballRivalsPublicManagerInterface;
  loading?: boolean;
};
export const SeasonProgressGraphWithLegend = ({ manager, loading }: Props) => {
  if (loading && !manager) {
    return (
      <Skeleton loading>
        <Box style={{ height: 'calc(32 * var(--unit))' }} />
      </Skeleton>
    );
  }
  if (
    !manager?.currentRanking ||
    !manager?.rankingHistory?.length ||
    manager?.rankingHistory?.length <= 1
  ) {
    return null;
  }

  return (
    <Box>
      <Vertical gap={2}>
        <Header>
          <ManagerDivision
            ranking={manager.currentRanking}
            divisionConfig={manager.divisionConfig}
            customTitle={
              <FormattedMessage
                id="SeasonProgressGraph.header.currentProgression"
                defaultMessage="Current Progression"
              />
            }
          />
        </Header>
        <SeasonProgressGraph manager={manager} />
      </Vertical>
    </Box>
  );
};

SeasonProgressGraphWithLegend.fragments = {
  footballRivalsPublicManagerInterface: gql`
    fragment SeasonProgressGraphWithLegend_footballRivalsPublicManagerInterface on FootballRivalsPublicManagerInterface {
      divisionConfig {
        id
        ...ManagerDivision_footballRivalsDivisionLeaderboardDivisionConfig
      }
      currentRanking {
        id
        ...ManagerDivision_footballRivalsDivisionRanking
      }
      ...SeasonProgressGraph_footballRivalsPublicManagerInterface
    }
    ${ManagerDivision.fragments.footballRivalsDivisionLeaderboardDivisionConfig}
    ${ManagerDivision.fragments.footballRivalsDivisionRanking}
    ${SeasonProgressGraph.fragments.footballRivalsPublicManagerInterface}
  ` as TypedDocumentNode<SeasonProgressGraphWithLegend_footballRivalsPublicManagerInterface>,
};
